import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { ProductStockLevel } from '../models/product-stock-level.model';
import { ProductStockMovement, MOVEMENT_ADJUSTMENT } from '../models/product-stock-movement.model';

/**
 * What the warehousing report screens can filter on. Every field is optional and an
 * unset field is left out of the query rather than sent as a wildcard.
 */
export class WarehousingReportFilters {
  /** -1 means "all", matching the selects on the existing warehousing screens. */
  warehouseId: number = -1;
  warehouseLocationId: number = -1;
  productId: number = -1;

  /** YYYY-MM-DD off a native date input. */
  startDate: string = '';
  endDate: string = '';

  referenceId: number = -1;
  movementType: string = '';
}

const LEVEL_JOINS: string = 'join=product&join=warehouseLocation&join=warehouseLocation.warehouse';
const MOVEMENT_JOINS: string = LEVEL_JOINS + '&join=reference';

@Injectable({
  providedIn: 'root'
})
export class WarehousingReportService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient) { }

  /**
   * Stock on hand. Reports are run to be totalled and exported, so no limit is sent and
   * nestjs-crud answers with the full array — a page would make the totals wrong.
   */
  getStockOnHand(filters: WarehousingReportFilters): Observable<ProductStockLevel[]> {
    const query: string[] = [LEVEL_JOINS, 'sort=id,DESC'];

    this.appendLocationFilters(query, filters);

    return this.httpClient.get(this._endpoint + 'product-stock-level?' + query.join('&'))
      .pipe(map((data) => {
        return <ProductStockLevel[]>data;
      }));
  }

  /** Movement history / audit trail. */
  getMovements(filters: WarehousingReportFilters): Observable<ProductStockMovement[]> {
    return this.queryMovements(filters, filters.movementType);
  }

  /**
   * Adjustments only. The type is pinned here rather than left to the caller so the
   * screen cannot accidentally widen the report to the whole ledger.
   *
   * KNOWN UNDER-REPORTING, to be removed by moving to POST warehouse-reporting/adjustments/
   * once that is reachable:
   *
   *   This matches on `movementType = 'adjustment'` alone, but stock is also written off as
   *   a *goods-out* against a reference code of SHORT, OVER, DAMAGED, STOCKTAKE or DISPOSAL.
   *   Those are adjustments by category rather than by type, and they are missing from this
   *   report. The consequence is not cosmetic: the value written off as DAMAGED/DISPOSAL is
   *   what backs an insurance claim or a customer chargeback, so this report currently
   *   understates it. The backend endpoint classifies on type OR reference code.
   */
  getAdjustments(filters: WarehousingReportFilters): Observable<ProductStockMovement[]> {
    return this.queryMovements(filters, MOVEMENT_ADJUSTMENT);
  }

  private queryMovements(filters: WarehousingReportFilters, movementType: string): Observable<ProductStockMovement[]> {
    const query: string[] = [MOVEMENT_JOINS, 'sort=id,DESC'];

    this.appendLocationFilters(query, filters);

    if (movementType) {
      query.push('filter=movementType||eq||' + encodeURIComponent(movementType));
    }

    if (filters.referenceId && filters.referenceId !== -1) {
      query.push('filter=referenceId||eq||' + filters.referenceId);
    }

    // createdAt is a MySQL datetime, so a bare end date of YYYY-MM-DD would compare as
    // midnight and drop everything that happened during that day. Both bounds are
    // widened to the day's edges instead, which keeps the range inclusive at both ends.
    if (filters.startDate) {
      query.push('filter=createdAt||gte||' + encodeURIComponent(this.startOfDay(filters.startDate)));
    }

    if (filters.endDate) {
      query.push('filter=createdAt||lte||' + encodeURIComponent(this.endOfDay(filters.endDate)));
    }

    return this.httpClient.get(this._endpoint + 'product-stock-movement?' + query.join('&'))
      .pipe(map((data) => {
        return <ProductStockMovement[]>data;
      }));
  }

  /**
   * A location already belongs to one warehouse, so once a location is picked filtering
   * on it alone is enough — the same reasoning the stock-levels screen uses.
   */
  private appendLocationFilters(query: string[], filters: WarehousingReportFilters) {
    if (filters.warehouseLocationId && filters.warehouseLocationId !== -1) {
      query.push('filter=warehouseLocationId||eq||' + filters.warehouseLocationId);
    } else if (filters.warehouseId && filters.warehouseId !== -1) {
      query.push('filter=warehouseLocation.warehouseId||eq||' + filters.warehouseId);
    }

    if (filters.productId && filters.productId !== -1) {
      query.push('filter=productId||eq||' + filters.productId);
    }
  }

  private startOfDay(date: string): string {
    return moment(date).startOf('day').format('YYYY-MM-DD HH:mm:ss');
  }

  private endOfDay(date: string): string {
    return moment(date).endOf('day').format('YYYY-MM-DD HH:mm:ss');
  }
}
